import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Modal from 'components/ui/Modal';
import { cn } from 'utils/cn';
import { Loader2, Users } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSegmentsLoading } from 'store/segments';

const CreateManualSegmentModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialName,
  initialDescription,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (segment: { name: string; description: string }) => void;
  initialName?: string;
  initialDescription?: string;
  initialId?: string;
}) => {
  const [segmentName, setSegmentName] = useState(initialName || '');
  const [description, setDescription] = useState(initialDescription || '');
  const segmentLoading = useSelector(selectSegmentsLoading);
  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    if (segmentName.trim()) {
      onSubmit({
        name: segmentName,
        description: description,
      });
      setSegmentName('');
      setDescription('');
    }
  };

  const handleCancel = () => {
    setSegmentName('');
    setDescription('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Manual Segment"
      className="max-w-md"
    >
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Segment Name"
          type="text"
          value={segmentName}
          onChange={(e) => setSegmentName(e.target.value)}
          placeholder="Enter segment name"
          required
          disabled={segmentLoading}
        />

        {/* Description */}
        <div className="space-y-2">
          <label
            htmlFor="segment-description"
            className="block text-sm font-medium text-foreground"
          >
            Description
          </label>
          <textarea
            id="segment-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter segment description (optional)"
            rows={3}
            disabled={segmentLoading}
            className={cn(
              'block w-full px-3 py-2 border border-border rounded-lg shadow-sm bg-card text-foreground placeholder-muted-foreground',
              'focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors resize-none',
              segmentLoading && 'bg-muted cursor-not-allowed'
            )}
          />
        </div>

        {/* Info Box */}
        <div className="flex items-start gap-3 p-4 bg-info-background border border-info/30 rounded-lg">
          <Users className="w-5 h-5 text-info mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-foreground mb-1">
              Manual Segment
            </h3>
            <p className="text-sm text-muted-foreground">
              You'll be able to manually add and remove contacts from this
              segment after creation.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={segmentLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!segmentName.trim() || segmentLoading}
            className="flex items-center gap-2"
          >
            {segmentLoading && <Loader2 className="w-4 h-4 animate-spin" />}{' '}
            {segmentLoading ? 'Creating...' : 'Create Segment'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateManualSegmentModal;
